export type LandingCard = {
  title: string;
  linkList: cardLinkList[];
  subTitle?: string;
};

export type cardLinkList = {
  summary?: string;
  icon?: string; // path to an image under /assets/images
  link: string;
  linkTitle?: string;
  description?: string;
  linkText?: string;
  target?: string;
  managerOnly?: boolean;
};

import {
  filterSectionsByAllowedLabels,
  getAllowedLabelsForPrimaryRole,
  normalizePrimaryRole,
} from "../utils/roleVisibility";
import { DEFAULT_MONTHLY_REPORT_LINK } from "../utils/fetchMonthlyReportLink";

/** The "My Teams" link, shared between the Teams section and the member-only UBT tools layout. */
const MY_TEAMS_LINK: cardLinkList = {
  linkTitle: "My Teams",
  description: "This is a collection of your bookmarked teams",
  icon: "/assets/images/ocd-illustrative-circle.svg",
  link: "/home/my-teams",
  linkText: "View My Teams",
};

export const ubtLandingCards: LandingCard[] = [
  {
    title: "Find a Unit Based Team",
    linkList: [
      {
        linkTitle: "Find and Select a UBT",
        description: "Find and select a UBT to view or take action.",
        icon: "/assets/images/search-illustrative-circle.svg",
        link: "/home/select-ubt",
        linkText: "Find and Select",
      },
      {
        linkTitle: "Search UBTs Projects",
        description: "Search UBTs by cost center names or numbers.",
        icon: "/assets/images/search-illustrative-circle.svg",
        link: "/home/search-teams",
        linkText: "Search by Project",
      },
      {
        linkTitle: "Teams Reporting",
        description: "Find UBTs by leaders, regions, areas, ratings or roles. ",
        icon: "/assets/images/search-illustrative-circle.svg",
        link: "/home/teams-reporting",
        linkText: "Teams Reporting",
      },
    ],
  },
  {
    title: "Teams",
    linkList: [
      MY_TEAMS_LINK,
      {
        linkTitle: "Edit UBT",
        description: "Make changes to an existing UBT profile.",
        icon: "/assets/images/clipboard-pencil-illustrative-circle.svg",
        link: "/home/edit-ubt",
        linkText: "Edit UBT",
      },
      {
        linkTitle: "Create New UBT",
        description: "Create a new team profile.",
        icon: "/assets/images/plus-circle-illustrative-circle.svg",
        link: "/home/add-profile",
        linkText: "Create New UBT",
      },
      {
        linkTitle: "Manage Access",
        description: "Add, remove or change roles of team members.",
        icon: "/assets/images/lock-closed-illustrative-circle.svg",
        link: "/home/manage-access",
        linkText: "Manage Team Members Access",
      },
    ],
  },
  {
    title: "UBT tools",
    linkList: [
      {
        linkTitle: "Path to Performance",
        description: "Select a UBT first, then review team evaluations.",
        icon: "/assets/images/target-outline-circle.svg",
        link: "/home/path-to-performance",
        linkText: "View team P2P evaluations",
      },
      {
        linkTitle: "Contact Us",
        description: "If you’re having issues, we can assist",
        icon: "/assets/images/envelope-illustrative-circle.svg",
        link: "/home/contact-us",
        linkText: "Contact Us",
      },
      {
        linkTitle: "Team Documents",
        description: "Instructions here",
        icon: "/assets/images/upload-illustrative-circle.svg",
        link: "/home/team-documents",
        linkText: "Upload Documents",
      },
    ],
  },
];

const LANDING_ALLOWED_LINKS_BY_ROLE = {
  lmp: [
    "Find and Select a UBT",
    "Search UBTs Projects",
    "Teams Reporting",
    "My Teams",
    "Manage Access",
    "Path to Performance",
    "Team Documents",
    "Contact Us"
  ],
  // For members the "Teams" section is hidden entirely; "My Teams" is injected
  // into "UBT tools" by getFilteredLandingCards, so it is intentionally absent here.
  member: [
    "Find and Select a UBT",
    "Search UBTs Projects",
    "Teams Reporting",
    "Contact Us",
    "Path to Performance",
    "Team Documents",
  ],
} as const;

/**
 * Applies member-style layout: removes the "Teams" section entirely and
 * prepends "My Teams" into the "UBT tools" section.
 */
function applyMemberLayout(cards: LandingCard[]): LandingCard[] {
  return cards
    .filter((card) => card.title !== "Teams")
    .map((card) =>
      card.title === "UBT tools"
        ? { ...card, linkList: [MY_TEAMS_LINK, ...card.linkList] }
        : card,
    );
}

/**
 * Returns the UBT landing cards filtered by the user's primary role.
 * - LMP: LMP-specific tiles (Find/Select UBT, Search Projects, Reporting, My Teams, Manage Access, P2P)
 * - Member/View-Only: member-focused tiles without the Teams section; My Teams appears at the top of UBT tools
 * - All other roles: full card list
 * @param primaryRole - The user's primaryRole from the profile slice.
 */
export function getFilteredLandingCards(primaryRole: string): LandingCard[] {
  const allowedLabels = getAllowedLabelsForPrimaryRole(
    primaryRole,
    LANDING_ALLOWED_LINKS_BY_ROLE,
  );

  if (!allowedLabels) {
    return ubtLandingCards;
  }

  const filtered = filterSectionsByAllowedLabels(ubtLandingCards, allowedLabels, {
    getItems: (card) => card.linkList,
    setItems: (card, linkList) => ({ ...card, linkList }),
    getItemLabel: (link) => link.linkTitle,
  });

  const normalizedRole = normalizePrimaryRole(primaryRole);
  // Apply member layout for all restricted roles (LMP has its own card set but no member layout).
  if (normalizedRole !== "lmp") {
    return applyMemberLayout(filtered);
  }

  return filtered;
}

/**
 * @param monthlyReportLink - The Monthly UBT Reports link URL (fetched from txt file).
 * @returns {cardLinkList[]} Array of resource card link configurations.
 */
export function getResourcesLandingCards(
  monthlyReportLink: string = DEFAULT_MONTHLY_REPORT_LINK
): cardLinkList[] {
  return [
    {
      linkText: "Monthly UBT Reports ",
      link: monthlyReportLink,
      description: "Archive of previously released monthly reports",
    },
    {
      linkText: "UBT Dashboard ",
      link: 'https://tableau.appl.kp.org/#/site/lmp_urdu/views/UBTDashboard/Dashboard',
      description: "An up-to-date, visual, and interactive look at key UBT program indicators.",
    },
    {
      linkText: "Project Template (PDF) ",
      link: `/assets/landingPagePDF/Performance_Improvement_Project_Template.pdf`,
      description: "Record project information before entering it in the UBT tracker",
    },
    {
      linkText: "Waste Reduction - PI Tool (PPT)",
      link: `/assets/landingPagePDF/Waste_Reduction_Tool_for_UBTs.ppt`,
      description: "Overview of Performance Improvement Tool",
    },
    {
      linkText: "UBT Tracker Quick Start Guide (PDF)",
      link: `/assets/landingPagePDF/UBTTrackerQuickStartGuide.pdf`,
      description: "The New Tracker on MYKP — Key Changes and How-To Steps",
    },
    {
      linkText: "UBT Tracker End User Guide (PDF)",
      link: `/assets/landingPagePDF/UBTTrackerGuide.pdf`,
      description: "Your guide to searching, reporting, and working in UBT Tracker",
    },
  ];
}

/** Static export of resource landing cards using the default Monthly Report link. */
export const resourcesLandingCards: cardLinkList[] = getResourcesLandingCards();

export default ubtLandingCards;